import {
  useContext,
  useLayoutEffect,
  useRef,
  type PropsWithChildren,
} from "react"
import type { Location } from "react-router-dom"
import { useLocation, useNavigationType } from "react-router-dom"

import { AnimationRouterContext } from "@be-miniapp/ui/components/animated-context"
import { cn } from "@be-miniapp/ui/lib/utils"
import {
  buildPageTransitionCss,
  getPageTransitionConfig,
  getPageTransitionMotion,
  PAGE_TRANSITION_TUNING,
  resolvePageTransitionDecision,
  buildExitTransitionCss,
  type PageTransitionDirection,
} from "@be-miniapp/ui/utils/page-transition"

const PAGE_LAYER_STYLES: React.CSSProperties = {
  backfaceVisibility: "hidden",
  WebkitBackfaceVisibility: "hidden",
  WebkitTransform: "translateZ(0)",
  transform: "translateZ(0)",
}

interface NativePageTransitionProps extends PropsWithChildren {
  className?: string
  getRouteKey?: (location: Location) => string
}

function NativePageTransition({
  children,
  className,
  getRouteKey = (location) => location.pathname,
}: NativePageTransitionProps) {
  const location = useLocation()
  const navigationType = useNavigationType()
  const routeKey = getRouteKey(location)
  const animationContext = useContext(AnimationRouterContext)
  const getAnimationState = animationContext?.getAnimationState
  const registerRouteTransitionElements =
    animationContext?.registerRouteTransitionElements
  const clearPreparedSnapshot = animationContext?.clearPreparedSnapshot
  const setAnimate = animationContext?.setAnimate
  const pageRef = useRef<HTMLDivElement>(null)
  const snapshotLayerRef = useRef<HTMLDivElement>(null)
  const previousRouteKeyRef = useRef(routeKey)
  const previousHistoryIndexRef = useRef(readHistoryIndex())
  const consumedAnimationVersionRef = useRef(getAnimationState?.().version ?? 0)

  // biome-ignore lint/correctness/useExhaustiveDependencies: routeKey keeps snapshot refs aligned with the active page layer.
  useLayoutEffect(() => {
    return registerRouteTransitionElements?.({
      pageElement: pageRef.current,
      snapshotLayerElement: snapshotLayerRef.current,
    })
  }, [registerRouteTransitionElements, routeKey])

  useLayoutEffect(() => {
    const previousRouteKey = previousRouteKeyRef.current
    const previousHistoryIndex = previousHistoryIndexRef.current
    const nextHistoryIndex = readHistoryIndex()
    const routeChanged = previousRouteKey !== routeKey

    previousRouteKeyRef.current = routeKey
    previousHistoryIndexRef.current = nextHistoryIndex

    if (!routeChanged) return

    const animationState = getAnimationState?.()
    const hasExplicitIntent =
      animationState != null &&
      animationState.version !== consumedAnimationVersionRef.current

    if (animationState) {
      consumedAnimationVersionRef.current = animationState.version
    }

    const decision = resolvePageTransitionDecision({
      explicitIntent: hasExplicitIntent
        ? {
            animate: animationState.animate,
            direction: animationState.direction,
          }
        : undefined,
      navigationType,
      previousHistoryIndex,
      nextHistoryIndex,
    })

    const pageElement = pageRef.current
    const snapshotElement = snapshotLayerRef.current
      ?.firstElementChild as HTMLElement | null

    if (!pageElement) {
      clearPreparedSnapshot?.()
      return
    }

    if (!decision.animate) {
      resetPageElement(pageElement)
      resetSnapshotLayer(snapshotLayerRef.current)
      clearPreparedSnapshot?.()
      return
    }

    return runPageTransition({
      pageElement,
      snapshotElement,
      snapshotLayerElement: snapshotLayerRef.current,
      direction: decision.direction,
      onComplete: () => {
        resetSnapshotLayer(snapshotLayerRef.current)
        clearPreparedSnapshot?.(snapshotElement)
        setAnimate?.({ animate: false, direction: decision.direction })
      },
    })
  }, [clearPreparedSnapshot, getAnimationState, navigationType, routeKey, setAnimate])

  return (
    <div
      className={cn(
        "relative isolate h-screen w-screen overflow-hidden bg-be-neutral-25",
        className
      )}
      data-native-page-transition=""
      style={{ background: PAGE_TRANSITION_TUNING.background }}
    >
      <div
        ref={snapshotLayerRef}
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden="true"
      />
      <div
        key={routeKey}
        ref={pageRef}
        className="absolute inset-0 h-full w-full overflow-hidden bg-be-neutral-25"
        style={{
          ...PAGE_LAYER_STYLES,
          background: PAGE_TRANSITION_TUNING.background,
          contain: "layout paint style",
        }}
      >
        {children}
      </div>
    </div>
  )
}

interface RunPageTransitionParams {
  pageElement: HTMLElement
  snapshotElement: HTMLElement | null
  snapshotLayerElement: HTMLElement | null
  direction: PageTransitionDirection
  onComplete: () => void
}

function applyMotionStart(
  element: HTMLElement,
  keyframes: {
    from: string
    opacityFrom: number
  },
  options?: {
    animateOpacity?: boolean
  }
) {
  const animateOpacity = options?.animateOpacity ?? true

  element.style.transition = "none"
  element.style.transform = keyframes.from
  element.style.opacity = String(keyframes.opacityFrom)
  element.style.willChange = animateOpacity ? "transform, opacity" : "transform"
}

function applyLayerOrder(
  pageElement: HTMLElement,
  snapshotLayerElement: HTMLElement | null,
  incomingZIndex: string,
  outgoingZIndex: string
) {
  pageElement.style.zIndex = incomingZIndex

  if (!snapshotLayerElement) return

  snapshotLayerElement.style.zIndex = outgoingZIndex
  snapshotLayerElement.style.transform = "translateZ(0)"
}

function applyMotionEnd(
  element: HTMLElement,
  transition: string,
  keyframes: {
    to: string
    opacityTo: number
  }
) {
  element.style.transition = transition
  element.style.transform = keyframes.to
  element.style.opacity = String(keyframes.opacityTo)
}

function runPageTransition({
  pageElement,
  snapshotElement,
  snapshotLayerElement,
  direction,
  onComplete,
}: RunPageTransitionParams) {
  const motion = getPageTransitionMotion(direction)
  const incomingConfig = getPageTransitionConfig(direction, "incoming")
  const outgoingConfig = getPageTransitionConfig(direction, "outgoing")
  const incomingTransition = buildPageTransitionCss(incomingConfig)
  const outgoingTransition = buildExitTransitionCss(outgoingConfig)
  const durationMs = Math.max(
    parseDurationMs(incomingConfig.duration),
    parseDurationMs(outgoingConfig.duration)
  )
  const isBackward = direction === "backward"
  let completed = false
  let frame = 0
  let incomingDone = isBackward
  let outgoingDone = !snapshotElement

  pageElement.style.pointerEvents = "none"
  pageElement.style.touchAction = "none"
  applyLayerOrder(
    pageElement,
    snapshotLayerElement,
    motion.incomingZIndex,
    motion.outgoingZIndex
  )

  if (isBackward) {
    pageElement.style.transition = "none"
    pageElement.style.transform = PAGE_TRANSITION_TUNING.final
    pageElement.style.opacity = "1"
    pageElement.style.willChange = ""
  } else {
    applyMotionStart(pageElement, motion.incoming)
  }

  if (snapshotElement) {
    snapshotElement.style.pointerEvents = "none"
    applyMotionStart(snapshotElement, motion.outgoing, {
      animateOpacity: motion.outgoing.opacityFrom !== motion.outgoing.opacityTo,
    })
  }

  void pageElement.offsetHeight
  snapshotElement && void snapshotElement.offsetHeight

  const finish = () => {
    if (completed) return
    completed = true
    resetPageElement(pageElement)
    onComplete()
  }

  const maybeFinish = () => {
    if (incomingDone && outgoingDone) {
      finish()
    }
  }

  const handleIncomingTransitionEnd = (event: TransitionEvent) => {
    if (event.target !== pageElement || event.propertyName !== "transform") {
      return
    }

    incomingDone = true
    maybeFinish()
  }

  const handleOutgoingTransitionEnd = (event: TransitionEvent) => {
    if (event.target !== snapshotElement || event.propertyName !== "transform") {
      return
    }

    outgoingDone = true
    maybeFinish()
  }

  if (!isBackward) {
    pageElement.addEventListener("transitionend", handleIncomingTransitionEnd)
  }
  snapshotElement?.addEventListener("transitionend", handleOutgoingTransitionEnd)

  frame = requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (!isBackward) {
        applyMotionEnd(pageElement, incomingTransition, motion.incoming)
      }

      if (snapshotElement) {
        applyMotionEnd(snapshotElement, outgoingTransition, motion.outgoing)
      }
    })
  })

  const fallback = window.setTimeout(finish, durationMs + 64)

  return () => {
    cancelAnimationFrame(frame)
    window.clearTimeout(fallback)
    pageElement.removeEventListener("transitionend", handleIncomingTransitionEnd)
    snapshotElement?.removeEventListener(
      "transitionend",
      handleOutgoingTransitionEnd
    )

    if (!completed) {
      resetPageElement(pageElement)
      resetSnapshotLayer(snapshotLayerElement)
    }
  }
}

function resetPageElement(pageElement: HTMLElement) {
  pageElement.style.transition = "none"
  pageElement.style.transform = PAGE_TRANSITION_TUNING.final
  pageElement.style.opacity = "1"
  pageElement.style.zIndex = ""
  pageElement.style.pointerEvents = ""
  pageElement.style.touchAction = ""
  pageElement.style.willChange = ""
}

function resetSnapshotLayer(snapshotLayerElement: HTMLElement | null) {
  if (!snapshotLayerElement) return
  snapshotLayerElement.style.zIndex = ""
  snapshotLayerElement.style.transform = ""
}

function readHistoryIndex(): number | null {
  if (typeof window === "undefined") return null

  const index = window.history.state?.idx
  return typeof index === "number" ? index : null
}

function parseDurationMs(duration: string): number {
  if (duration.endsWith("ms")) {
    return Number.parseFloat(duration)
  }

  if (duration.endsWith("s")) {
    return Number.parseFloat(duration) * 1000
  }

  return 320
}

export { NativePageTransition }
